import {
   Button,
   ContentCard,
   Grid,
   Group,
   Radio,
   Stack,
} from '@forwardflow/ui-v2'
import { ArrowLeftMdIcon, ChevronRightIcon } from '@forwardflow/icons'
import { motion } from 'framer-motion'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { useState } from 'react'
import {
   getFilteredVariants,
   getPlaceholderMap,
   renderTemplateWithPlaceholders,
} from '@forwardflow/utils/introductionTemplates'

export const PresentationStep = () => {
   const { getNextStep, getPreviousStep, onboardingData, setBusinessDetails } =
      useOnboardingStoreV2()
   const { businessDetails } = onboardingData
   const { hasTeam } = businessDetails
   const [selectedVariant, setSelectedVariant] = useState<string | null>(
      businessDetails?.introductionTemplate || null
   )

   const filteredVariants = getFilteredVariants(Boolean(hasTeam))
   const placeHolderMap = getPlaceholderMap(businessDetails)

   const onContinue = () => {
      setBusinessDetails({
         ...businessDetails,
         introductionTemplate: selectedVariant || '',
      })
      getNextStep()
   }

   return (
      <Stack>
         <ContentCard
            className="space-y-2"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
         >
            <p className="text-white font-medium">
               How do you want us to present ourselves on your behalf?
            </p>
            <p className="text-[#99A1AF] text-sm font-medium max-w-[600px]">
               Choose how leads hear your intro from the below options
            </p>
         </ContentCard>
         <ContentCard
            className="space-y-4 !p-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.6 }}
         >
            <Stack>
               <Stack
                  className="rounded-[16px] border border-[#A684FF]/30 bg-[rgba(166,132,255,0.05)] p-4"
                  gap={12}
               >
                  <span className="inline-block px-2 rounded-[4px] bg-white/10 text-white/60 font-medium text-xs py-0.5 w-fit">
                     Assistant Name
                  </span>

                  <p className="text-[#99A1AF] text-sm font-medium">
                     The assistant's name will be set when you launch your first
                     campaign. We're using a placeholder in previews.
                  </p>
               </Stack>
               <Stack>
                  <p className="text-white font-medium">
                     Select an Introduction template
                  </p>
                  {filteredVariants.map((variant, i) => (
                     <motion.button
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                           duration: 0.3 + i * 0.12,
                           delay: 0.9 + i * 0.06,
                           ease: 'easeOut',
                        }}
                        onClick={() => setSelectedVariant(variant.value)}
                        key={variant.value}
                        className="rounded-lg text-left border border-white/[0.1] bg-white/[0.02] px-4 py-4"
                     >
                        <Stack>
                           <Group key={variant.value}>
                              <Radio
                                 value={variant.value}
                                 readOnly
                                 checked={selectedVariant === variant.value}
                              />
                              <p>
                                 {renderTemplateWithPlaceholders(variant.value, placeHolderMap)}
                              </p>
                           </Group>
                           {i === 0 && (
                              <p className="text-[#99A1AF] text-sm font-medium ml-8">
                                 We auto-insert the assigned agent's name from
                                 your CRM
                              </p>
                           )}
                        </Stack>
                     </motion.button>
                  ))}
               </Stack>
            </Stack>
         </ContentCard>
         <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.9 }}
         >
            <Grid>
               <Grid.Col span={3}>
                  <Button
                     variant="secondary"
                     className="w-full"
                     onClick={() => getPreviousStep()}
                  >
                     <ArrowLeftMdIcon width={12} /> Back
                  </Button>
               </Grid.Col>
               <Grid.Col span={9}>
                  <Button
                     className="w-full"
                     onClick={() => {
                        onContinue()
                     }}
                     disabled={!selectedVariant}
                  >
                     Save & Continue <ChevronRightIcon width={18} />
                  </Button>
               </Grid.Col>
            </Grid>
         </motion.div>
      </Stack>
   )
}
